import styled from '@emotion/styled';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Tab, Tabs } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OrganizationForm from './create-organization/OrganizationForm';
import AssignUserToOrganizationForm from './assign-user-to-organization/AssignUserToOrganizationForm';
import InviteUserToOrganizationForm from './invite-user-to-tenant/InviteUserToOrganizationForm';
import OrganizationBreadcrumb from '../OrganizationBreadcrumb';
import { getColor } from '../../../theme/colors/colors';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `org-tab-${index}`,
    'aria-controls': `org-tabpanel-${index}`,
  };
}

const tabs = [
  { label: 'Create', formId: 'create-organization-form', actionLabel: 'Create' },
  { label: 'Assign', formId: 'assign-user-form', actionLabel: 'Assign' },
  { label: 'Invite', formId: 'invite-user-form', actionLabel: 'Invite' },
];

const Organization = () => {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <PageWrapper>
      <OrganizationBreadcrumb
        items={[
          { label: 'Organizations', to: '/organization/list' },
          { label: 'Add Organization' },
        ]}
      />
      <SubHeader>
        <HeaderLeft>
          <CardTitle>Organization Management</CardTitle>
          <CardSubtitle>Create, assign, or invite users to organizations</CardSubtitle>
        </HeaderLeft>
        <HeaderRight>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/organization/list')}
            sx={{
              backgroundColor: '#FFFFFF',
              color: 'text.primary',
              borderColor: '#0000001A',
              '&:hover': { backgroundColor: '#F5F5F5', borderColor: '#0000003A' },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={tabs[value].formId}
            variant="contained"
            color="primary"
            size="large"
          >
            {tabs[value].actionLabel}
          </Button>
        </HeaderRight>
      </SubHeader>
      <Card>
        <TabsWrapper>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="organization-management"
            sx={{
              minHeight: '36px',
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTabs-flexContainer': { gap: '4px' },
              '& .MuiTab-root': {
                minHeight: '36px',
                padding: '6px 16px',
                borderRadius: '4px',
                color: 'rgba(0,0,0,0.6)',
                textTransform: 'none',
                fontSize: '14px',
                '&.Mui-selected': {
                  backgroundColor: '#FFFFFF',
                  color: 'rgba(0,0,0,0.87)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                },
              },
            }}
          >
            <Tab label="Create" {...a11yProps(0)} />
            <Tab label="Assign" {...a11yProps(1)} />
            <Tab label="Invite" {...a11yProps(2)} />
          </Tabs>
        </TabsWrapper>

        <CustomTabPanel value={value} index={0}>
          <OrganizationForm />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <AssignUserToOrganizationForm />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <InviteUserToOrganizationForm />
        </CustomTabPanel>
      </Card>
    </PageWrapper>
  );
};

export default Organization;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  height: 100%;
`;

const Card = styled.div`
  background-color: #ffffff;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
    0px 1px 10px 0px rgba(0, 0, 0, 0.12);
`;

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid #0000001a;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h4`
  color: ${getColor('text')};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.31px;
  margin: 0;
`;

const CardSubtitle = styled.p`
  color: ${getColor('text')};
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  opacity: 0.6;
  margin: 4px 0 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabsWrapper = styled.div`
  background-color: #f5f5f5;
  padding: 8px 16px;
  border-bottom: 1px solid #0000001a;
`;
